import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Incident, Resident } from '../types';
import CustomSelect from '../components/CustomSelect';

const Incidents: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [residents, setResidents] = useState<Resident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [incidentsResponse, residentsResponse] = await Promise.all([
          api.get('/incidents'),
          api.get('/residents')
        ]);
        setIncidents(incidentsResponse.data.data);
        setResidents(residentsResponse.data.data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCreateIncident = () => {
    setShowCreateModal(true);
  };

  const handleViewDetails = (incident: Incident) => {
    setSelectedIncident(incident);
    setShowDetailsModal(true);
  };

  const getSeverityBadgeColor = (severity: string) => {
    switch (severity) {
      case 'low':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'critical':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'investigating':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredIncidents = incidents.filter(incident => {
    const statusMatch = filterStatus === 'all' || incident.status === filterStatus;
    const severityMatch = filterSeverity === 'all' || incident.severity === filterSeverity;
    return statusMatch && severityMatch;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Accidents & Incidents
          </h2>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            type="button"
            onClick={handleCreateIncident}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Report Accident/Incident
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4 sm:space-y-0 sm:flex sm:space-x-4">
        <div className="flex-1">
          <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700">
            Status
          </label>
          <div className="mt-1">
            <CustomSelect
              id="status-filter"
              value={filterStatus}
              onChange={setFilterStatus}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'open', label: 'Open' },
                { value: 'investigating', label: 'Investigating' },
                { value: 'resolved', label: 'Resolved' },
                { value: 'closed', label: 'Closed' },
              ]}
            />
          </div>
        </div>
        <div className="flex-1">
          <label htmlFor="severity-filter" className="block text-sm font-medium text-gray-700">
            Severity
          </label>
          <div className="mt-1">
            <CustomSelect
              id="severity-filter"
              value={filterSeverity}
              onChange={setFilterSeverity}
              options={[
                { value: 'all', label: 'All Severities' },
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
                { value: 'critical', label: 'Critical' },
              ]}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="bg-white card-list overflow-hidden">
        <ul className="divide-y divide-gray-200">
          {filteredIncidents.length === 0 ? (
            <li className="p-6 text-center text-gray-500">
              No incidents found
            </li>
          ) : (
            filteredIncidents.map((incident) => (
              <li key={incident._id}>
                <div className="px-4 py-4 hover:bg-gray-50 cursor-pointer" onClick={() => handleViewDetails(incident)}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {incident.title}
                        </p>
                        <div className="flex space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityBadgeColor(incident.severity)}`}>
                            {incident.severity}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(incident.status)}`}>
                            {incident.status}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500">
                        <div className="flex-1">
                          <p className="mb-1">
                            <span className="font-medium">Resident:</span> {incident.residentName}
                          </p>
                          <p className="mb-1">
                            <span className="font-medium">Type:</span> {incident.incidentType}
                          </p>
                          <p className="mb-1">
                            <span className="font-medium">Date:</span> {formatDate(incident.incidentDate)}
                          </p>
                          {incident.location && (
                            <p className="mb-1">
                              <span className="font-medium">Location:</span> {incident.location}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400">
                            Reported by: {incident.reportedByName}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatDate(incident.createdAt)}
                          </p>
                          <div className="mt-2 flex space-x-4 text-xs">
                            {incident.injuriesReported && (
                              <span className="text-red-600">⚠️ Injuries</span>
                            )}
                            {incident.medicalAttention && (
                              <span className="text-orange-600">🏥 Medical</span>
                            )}
                            {incident.familyNotified && (
                              <span className="text-blue-600">👨‍👩‍👧‍👦 Family Notified</span>
                            )}
                            {incident.followUpRequired && (
                              <span className="text-primary-600">📋 Follow-up</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>

      {/* Details Modal Placeholder */}
      {showDetailsModal && selectedIncident && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Accident/Incident Details
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">{selectedIncident.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{selectedIncident.description}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Resident:</span>
                    <p className="text-sm text-gray-900">{selectedIncident.residentName}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Reported by:</span>
                    <p className="text-sm text-gray-900">{selectedIncident.reportedByName}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Type:</span>
                    <p className="text-sm text-gray-900">{selectedIncident.incidentType}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Severity:</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityBadgeColor(selectedIncident.severity)}`}>
                      {selectedIncident.severity}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Status:</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(selectedIncident.status)}`}>
                      {selectedIncident.status}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Date:</span>
                    <p className="text-sm text-gray-900">{formatDate(selectedIncident.incidentDate)}</p>
                  </div>
                </div>

                {selectedIncident.location && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">Location:</span>
                    <p className="text-sm text-gray-900">{selectedIncident.location}</p>
                  </div>
                )}

                {selectedIncident.actionsTaken && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">Actions Taken:</span>
                    <p className="text-sm text-gray-900">{selectedIncident.actionsTaken}</p>
                  </div>
                )}

                {selectedIncident.followUpNotes && (
                  <div>
                    <span className="text-sm font-medium text-gray-500">Follow-up Notes:</span>
                    <p className="text-sm text-gray-900">{selectedIncident.followUpNotes}</p>
                  </div>
                )}

                <div className="border-t pt-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-2">Additional Information</h5>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center">
                      <span className={`mr-2 ${selectedIncident.injuriesReported ? 'text-red-600' : 'text-gray-400'}`}>
                        {selectedIncident.injuriesReported ? '✓' : '✗'}
                      </span>
                      Injuries Reported
                    </div>
                    <div className="flex items-center">
                      <span className={`mr-2 ${selectedIncident.medicalAttention ? 'text-red-600' : 'text-gray-400'}`}>
                        {selectedIncident.medicalAttention ? '✓' : '✗'}
                      </span>
                      Medical Attention Required
                    </div>
                    <div className="flex items-center">
                      <span className={`mr-2 ${selectedIncident.familyNotified ? 'text-blue-600' : 'text-gray-400'}`}>
                        {selectedIncident.familyNotified ? '✓' : '✗'}
                      </span>
                      Family Notified
                    </div>
                    <div className="flex items-center">
                      <span className={`mr-2 ${selectedIncident.followUpRequired ? 'text-primary-600' : 'text-gray-400'}`}>
                        {selectedIncident.followUpRequired ? '✓' : '✗'}
                      </span>
                      Follow-up Required
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal Placeholder */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Report New Accident/Incident
                </h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Accident/incident reporting form will be implemented here.
              </p>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  disabled
                >
                  Create Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Incidents;